import { useState } from "react";
import { Pressable, View } from "react-native";
import { useTranslation } from "react-i18next";
import Icon from "@shared/components/md-design-system/Icon/Icon";
import { WalkIcon } from "@shared/components/md-design-system/Icon/ActivityIcon";
import { Text } from "@shared/components/text";
import { colorPalette } from "@shared/config/colorsConfig";
import { IPathwayActivity } from "app/_modules/pathways/entities";
import { useAuth } from "app/auth/hooks/useAuth";
import { ActivityName } from "../../../types";
import { HOME_STEPS } from "../../../constants";
import { ProgressBar } from "@md-design-system/ProgressBar";
import { StepsModal } from "../StepsModal";

export const StepCard = ({ activity, allowPress = true }: { activity: IPathwayActivity; allowPress?: boolean }) => {
    const { i18n } = useTranslation();
    const { meData } = useAuth();
    const [modalVisible, setModalVisible] = useState(false);

    const stepValue = (activity.measurement)?.amount || 0;
    const vitalParameterData = meData?.current_pathway?.vital_parameters?.find(
        (vitalParameter) => vitalParameter.vital_parameter.name === ActivityName.STEP
    );
    const stepGoal = vitalParameterData?.amount;

    const reached = !!stepGoal && stepValue >= stepGoal;
    const percentage = stepGoal ? (stepValue / stepGoal) * 100 : 0;
    const accent = reached ? HOME_STEPS.reachedAccent : HOME_STEPS.accent;

    return (
        <>
            <Pressable
                onPress={() => setModalVisible(true)}
                disabled={!allowPress}
                className="border border-gray-300 bg-white rounded-2xl px-18 py-18"
            >
                <View className="flex-row items-center gap-3">
                    <WalkIcon size={HOME_STEPS.iconGlyph} color={accent} />

                    <View className="flex-1">
                        <View className="flex-row items-center gap-2">
                            <Text className="font-bold text-black text-2xl">
                                {new Intl.NumberFormat(i18n.language).format(stepValue)}
                            </Text>
                            {!!stepGoal && (
                                <Text className="text-md text-gray-500">
                                    {`/ ${new Intl.NumberFormat(i18n.language).format(stepGoal)}`}
                                </Text>
                            )}
                            {reached && (
                                <Icon name="check-circle" size={24} color={HOME_STEPS.reachedAccent} />
                            )}
                        </View>

                        <View className="mt-2">
                            <ProgressBar
                                percentage={percentage}
                                complete={reached}
                                trackColor={colorPalette.gray[300]}
                                fillColor={HOME_STEPS.accent}
                                completeFillColor={HOME_STEPS.reachedAccent}
                                completeTrackColor={HOME_STEPS.reachedTrack}
                            />
                        </View>
                    </View>

                    {allowPress && (
                        <Icon name="right" size={HOME_STEPS.checkGlyph} color={colorPalette.gray[500]} />
                    )}
                </View>
            </Pressable>

            <StepsModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                current={stepValue}
                goal={stepGoal ?? undefined}
            />
        </>
    );
};

export default StepCard;
